<script setup>
import {computed, ref} from "vue";
import {useAuthService} from "~/composables/useAuthService";
import {useTimelineModels} from "~/composables/useTimelineModels";
import {useAmericanoFormat} from "~/composables/useAmericanoFormat";
import PlayerCard from "@/components/players/PlayerCard.vue";
import {useHandicapSettingsFor} from "~/composables/useHandicapSettingsFor";
import {useHandicaps} from "~/composables/useHandicaps";

// Americano division standings (adapted from leagues/StandingsTable): columns
// PLAYER · P (played) · F (points for) · A (points against) · +/-, plus S± and
// G± (set and game differentials) when the division's results carry set rows
// (hasSetScores). Ranking runs points → set diff → game diff → points diff
// server-side. Rows are AmericanoCyclePlayer standings rows, ALREADY
// ordered by position by the API. Players render as tappable player-cards
// (full player dialog, fetched on tap). The current user's row — and any
// highlightPlayerIds — get a full-width brand tint; ranks below the top 3
// dim. Withdrawn players stay listed (their points are kept) but render muted
// with a "Withdrawn" chip. No squash-levels column: americano ranking never
// uses squash levels.
const props = defineProps({
  // AmericanoCyclePlayerResponse rows, position-ordered.
  players: {type: Array, required: true},
  itemLimit: {type: Number, default: 999},
  // Extra player ids to brand-tint (the current user's own row always is).
  highlightPlayerIds: {type: Array, default: () => []},
  // Division flag: results carry per-set rows, so the set/game tiebreak
  // columns are meaningful. Exact-score divisions keep the classic layout.
  hasSetScores: {type: Boolean, default: false},
  // The americano league (for handicap labels); null = no handicap chips.
  americanoId: {type: String, default: null},
  scoringSystemId: {type: String, default: null},
});

// Player handicaps: labels from the league's rendered settings (cached per league).
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => props.americanoId ? {americanoId: props.americanoId} : null),
    computed(() => props.americanoId != null));
const {labelFor: handicapLabelFor, chipClassFor} = useHandicaps();

// Small screens can't fit the full column set next to the names, so mobile
// collapses to F (the ranking number) with an "‹ Expand" chip — tapping it
// slides the whole column set in OVER the player names; "›" closes it. All
// of it is CSS-gated to the mobile breakpoint: desktop renders classic.
const expanded = ref(false);

const {user} = useAuthService();
const {playerInitials} = useTimelineModels();
const {signedDiff} = useAmericanoFormat();

// Sparse player object for the tappable card — the full record (contact,
// messaging) loads only when the dialog opens.
const cardPlayer = (player) => ({
  id: player.playerId,
  fullName: player.fullName,
  imageFileName: player.imageFileName,
  initials: playerInitials(player),
  // Carried through so the card never mislabels a linked member as
  // "admin managed" while (or if) the full record fetch is pending/failed.
  userId: player.userId ?? null,
  hasInvite: player.hasInvite ?? false,
});

const handicapEntries = computed(() => (props.players || []).map((p) => ({
  teamId: p.teamId ?? p.playerId ?? p.id, playerId: p.playerId ?? p.id ?? null,
  name: p.fullName ?? p.name ?? [p.firstName, p.lastName].filter(Boolean).join(' '), value: p.handicap ?? null,
})));
const rows = computed(() => (props.players || []).slice(0, props.itemLimit).map((player, i) => {
  const isYou = player.playerId === user.value?.playerId;
  return {
    player,
    position: player.position ?? (i + 1),
    withdrawn: player.withdrawnAt != null,
    isYou,
    highlighted: isYou || props.highlightPlayerIds.includes(player.playerId),
  };
}));
</script>

<template>
  <div class="tl-standings">
    <div class="ast-head">
      <div class="ast-pos"></div>
      <div class="ast-name">Player</div>
      <!-- mobile collapsed: expand chip + just the F column -->
      <button type="button" class="ast-expand" aria-label="Show all columns" @click="expanded = true">
        <v-icon size="13">mdi-chevron-left</v-icon>
        Expand
      </button>
      <div class="ast-num ast-num--for ast-solo">F</div>
      <!-- the full column set — static on desktop, a slide-over panel on mobile -->
      <div class="ast-stats" :class="{ 'ast-stats--open': expanded }">
        <button type="button" class="ast-collapse" aria-label="Hide extra columns" @click="expanded = false">
          <v-icon size="15">mdi-chevron-right</v-icon>
        </button>
        <v-tooltip text="Matches played" location="top">
          <template #activator="{ props: tip }">
            <div v-bind="tip" class="ast-num">P</div>
          </template>
        </v-tooltip>
        <v-tooltip text="Points for: awarded by the scoring system, and the table ranks on this first" location="top">
          <template #activator="{ props: tip }">
            <div v-bind="tip" class="ast-num">F</div>
          </template>
        </v-tooltip>
        <v-tooltip text="Points against: conceded to opponents" location="top">
          <template #activator="{ props: tip }">
            <div v-bind="tip" class="ast-num">A</div>
          </template>
        </v-tooltip>
        <v-tooltip
            :text="hasSetScores
                ? 'Points difference: the third tiebreaker, after set and game difference'
                : 'Points difference: the tiebreaker on equal points'"
            location="top">
          <template #activator="{ props: tip }">
            <div v-bind="tip" class="ast-num">+/-</div>
          </template>
        </v-tooltip>
        <template v-if="hasSetScores">
          <v-tooltip text="Set difference: the first tiebreaker on equal points" location="top">
            <template #activator="{ props: tip }">
              <div v-bind="tip" class="ast-num">S±</div>
            </template>
          </v-tooltip>
          <v-tooltip text="Game difference: the second tiebreaker (a champions tiebreak counts as one game)" location="top">
            <template #activator="{ props: tip }">
              <div v-bind="tip" class="ast-num">G±</div>
            </template>
          </v-tooltip>
        </template>
      </div>
    </div>
    <div
        v-for="row in rows"
        :key="row.player.membershipId"
        class="ast-row"
        :class="{ 'ast-row--me': row.highlighted, 'ast-row--out': row.withdrawn }"
    >
      <div class="ast-pos" :class="{ 'ast-pos--low': row.position > 3 }">{{ row.position }}</div>
      <div class="ast-name">
        <player-card :player="cardPlayer(row.player)" size="small" :max-lines="1" fetch-on-expand/>
        <span v-if="row.withdrawn" class="tl-chip ast-out-chip">Withdrawn</span>
        <span v-if="handicapLabelFor(handicapSettings, row.player.handicap)" class="tl-chip ast-hcp" :class="chipClassFor(handicapSettings, row.player.handicap)">
          {{ handicapLabelFor(handicapSettings, row.player.handicap) }}
        </span>
      </div>
      <div class="ast-num ast-num--for ast-solo">{{ row.player.points ?? 0 }}</div>
      <div class="ast-stats" :class="{ 'ast-stats--open': expanded }">
        <div class="ast-num">{{ row.player.played }}</div>
        <div class="ast-num ast-num--for">{{ row.player.points ?? 0 }}</div>
        <div class="ast-num">{{ row.player.pointsAgainst ?? 0 }}</div>
        <div class="ast-num">{{ signedDiff(row.player.pointsDiff ?? 0) }}</div>
        <template v-if="hasSetScores">
          <div class="ast-num">{{ signedDiff(row.player.setsDiff ?? 0) }}</div>
          <div class="ast-num">{{ signedDiff(row.player.gamesDiff ?? 0) }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tl-standings {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;
}

.ast-head {
  display: flex;
  align-items: center;
  padding: 10px 14px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.ast-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;

  & + .ast-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &--me {
    background: rgb(var(--v-theme-brandTint));

    .ast-pts {
      color: rgb(var(--v-theme-primary));
    }
  }

  /* Withdrawn: muted but present — their points are kept in the table */
  &--out {
    .ast-name,
    .ast-num {
      color: rgb(var(--v-theme-textSubtle));
      font-weight: 500;
    }
  }
}

.ast-pos {
  width: 26px;
  flex-shrink: 0;
  font-size: 12.5px;
  font-weight: 700;

  &--low {
    color: rgb(var(--v-theme-textSubtle));
  }
}

.ast-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}

.ast-out-chip {
  font-size: 10px;
  flex-shrink: 0;
}

.ast-num {
  width: 34px;
  flex-shrink: 0;
  text-align: center;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  font-variant-numeric: tabular-nums;

  /* F is the ranking number — it reads like the old Pts column */
  &--for {
    font-size: 13.5px;
    font-weight: 800;
  }
}

/* The full column set: inline on desktop, a slide-over panel on mobile. */
.ast-stats {
  display: flex;
  align-items: center;
}

/* Mobile-only affordances (hidden on desktop) */
.ast-expand,
.ast-collapse,
.ast-solo {
  display: none;
}

@media (max-width: 599px) {
  .ast-head,
  .ast-row {
    position: relative;
  }

  /* Collapsed: just the F column next to the names */
  .ast-solo {
    display: block;
  }

  .ast-expand {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-right: 6px;
    padding: 2px 8px 2px 3px;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: rgb(var(--v-theme-textSubtle));
    background: rgb(var(--v-theme-light));
    border: 1px solid rgb(var(--v-theme-borderLight));
    border-radius: 999px;
    cursor: pointer;
  }

  /* Contract control: a plain chevron inside the bookmark, padded off the
     container's rounded left edge (the CONTAINER carries the curves). */
  .ast-collapse {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 19px;
    height: 22px;
    margin: 0 6px 0 10px;
    padding: 0;
    color: rgb(var(--v-theme-textSubtle));
    background: none;
    border: none;
    cursor: pointer;
  }

  /* Expanded: the columns slide in from the right OVER the player names —
     a bookmark sticking out of the table's right side. The clip-path lets
     the box shadow escape LEFT only: clipping its vertical bleed is what
     stops stacked header/row shadows banding at the dividers, so the whole
     thing reads as ONE panel with one continuous shadow that follows the
     rounded corners. */
  .ast-stats {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    padding-right: 14px;
    background: rgb(var(--v-theme-surface));
    box-shadow: -8px 0 12px -6px rgba(0, 0, 0, 0.16);
    clip-path: inset(0 0 0 -30px);
    transform: translateX(105%);
    transition: transform 0.22s ease-out;
    pointer-events: none;

    &--open {
      transform: translateX(0);
      pointer-events: auto;
    }
  }

  /* The bookmark's curves: top-left on the header, bottom-left on the last row */
  .ast-head .ast-stats {
    border-top-left-radius: 12px;
  }

  .ast-row:last-child .ast-stats {
    border-bottom-left-radius: 12px;
  }

  .ast-row--me .ast-stats {
    background: rgb(var(--v-theme-brandTint));
  }

  /* Rows have no close button, but reserve its slot (10px pad + 19px icon +
     6px gap) so the header and row panels share ONE flush left edge */
  .ast-row .ast-stats {
    padding-left: 35px;
  }

  .ast-row .ast-collapse {
    display: none;
  }
}
.ast-hcp {
  font-size: 10px;
  font-weight: 700;
  padding: 0 6px;
  border-radius: 999px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}
.ast-tools { display: flex; justify-content: flex-end; padding: 6px 10px 0; }
</style>
